import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, ChevronDown, Sparkles, Archive, Sliders, Plus, ImageIcon, UploadCloud, Loader2, X, Scan, CheckCircle2, ImagePlus, Pencil, AlertCircle, User } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
  type CrearAnuncioModeloState,
  type CrearAnuncioObjetivo,
} from '../utils/crearAnuncioStorage';
import { trackInteraction } from '../utils/trackInteraction';

// Tipos para la personalización
// La selección de poses fue movida a la nueva pantalla SeleccionarPoseModelo

type Genero = 'Hombre' | 'Mujer' | '';
type Edad = 'Bebé' | 'Niño' | 'Joven (25 años)' | 'Adulto/a(30 a 40 años)' | 'Mayor' | '';
type TipoCuerpo = 'Promedio' | 'Talle Grande' | 'Talle Extra Grande' | '';

export const PersonalizarModeloAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsRaw = params.get('ids');
  const ids = useMemo(
    () => {
      if (!idsRaw) return [] as string[];
      return idsRaw.split(',').map((s) => s.trim()).filter(Boolean);
    },
    [idsRaw]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'personalizado';
  const composicion = (params.get('composicion') as 'modelo' | 'producto') || 'modelo';
  const tipo = (params.get('tipo') as 'imagen' | 'video') || 'imagen';
  const formato = (params.get('formato') as 'post' | 'story' | 'square') || 'post';
  const cantidadImagenesRaw = params.get('cantidad_imagenes');
  const cantidadImagenes = useMemo(() => {
    const n = Number(cantidadImagenesRaw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [cantidadImagenesRaw]);
  const objetivo = params.get('objetivo') || '';
  const detalles_oferta = params.get('detalles_oferta') || '';
  const estudioVirtual = params.get('estudio_virtual') === '1';

  const savedStateRef = useRef(loadLastCrearAnuncioState());
  const savedModelo = savedStateRef.current?.modelo;
  const savedModeloGuardadoId = savedModelo?.guardadoId;
  const savedModeloGuardadoNombre = savedModelo?.guardadoNombre;

  // Características del modelo (similares a ImageConfiguration)
  const [genero, setGenero] = useState<Genero>(() => (savedModelo?.genero as Genero | undefined) ?? '');
  const [edad, setEdad] = useState<Edad>(() => (savedModelo?.edad as Edad | undefined) ?? '');
  const [tipoCuerpo, setTipoCuerpo] = useState<TipoCuerpo>(() => (savedModelo?.tipoCuerpo as TipoCuerpo | undefined) ?? 'Promedio');
  const [tonoPiel, setTonoPiel] = useState<string>(() => savedModelo?.tonoPiel ?? '');
  const [colorPelo, setColorPelo] = useState<string>(() => savedModelo?.colorPelo ?? '');
  const [largoPelo, setLargoPelo] = useState<string>(() => savedModelo?.largoPelo ?? '');
  const [descModelo, setDescModelo] = useState<string>(() => savedModelo?.descripcion ?? '');

  // Opciones de tono de piel y color de pelo (mapeadas con color)
  const skinToneOptions = [
    { label: 'Muy clara', color: '#FFDFC4' },
    { label: 'Clara', color: '#F0C8A0' },
    { label: 'Beige', color: '#DEB887' },
    { label: 'Trigueña', color: '#CD853F' },
    { label: 'Oscura', color: '#8B4513' },
    { label: 'Muy oscura', color: '#3C2414' },
  ];
  const hairColorOptions = [
    { label: 'Negro', color: '#232323' },
    { label: 'Castaño oscuro', color: '#4B2E09' },
    { label: 'Castaño claro', color: '#A0522D' },
    { label: 'Rubio', color: '#F8E49B' },
    { label: 'Colorado', color: '#B55239' },
  ];

  // Modo de selección de modelo
  type ModeloMode = 'ia' | 'guardados' | 'personalizado';
  const [modeloMode, setModeloMode] = useState<ModeloMode>(() => (savedModelo?.mode as ModeloMode | undefined) ?? 'ia');

  // Modelos guardados
  // Tipo con imagen firmada para mostrar en cards
  type SavedModelItem = { id: number; nombre: string; fotoFileName?: string; signedUrl?: string };
  const [savedModels, setSavedModels] = useState<SavedModelItem[]>([]);
  const [loadingSavedModels, setLoadingSavedModels] = useState<boolean>(false);
  const [savedModelsError, setSavedModelsError] = useState<string | null>(null);
  const [savedModelsFetched, setSavedModelsFetched] = useState<boolean>(false);
  const [selectedSavedModelId, setSelectedSavedModelId] = useState<number | null>(() => {
    if (!savedModeloGuardadoId) return null;
    const parsed = Number(savedModeloGuardadoId);
    return Number.isFinite(parsed) ? parsed : null;
  });
  // Modal para añadir modelo
  const [showAddModelCard, setShowAddModelCard] = useState<boolean>(false);
  const [addModelMode, setAddModelMode] = useState<'choice' | 'fromGenerated' | 'uploadPhotos'>('choice');
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Editar modelo guardado (modal)
  const [showEditModelModal, setShowEditModelModal] = useState(false);
  const [editingModel, setEditingModel] = useState<SavedModelItem | null>(null);
  const [editModelName, setEditModelName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Grid de imágenes generadas (para "Guardar modelo de imagen generada")
  type GenImageItem = { id: string; fileName: string; thumbUrl: string; fullUrl: string };
  const [genImages, setGenImages] = useState<GenImageItem[]>([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genLoadingMore, setGenLoadingMore] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genHasMore, setGenHasMore] = useState(true);
  const [genPage, setGenPage] = useState(0);
  const GEN_PAGE_SIZE = 32;
  const genContainerRef = useRef<HTMLDivElement | null>(null);
  const genLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const [selectedGenImage, setSelectedGenImage] = useState<GenImageItem | null>(null);
  const [savingModelFromImage, setSavingModelFromImage] = useState(false);
  const [previewSuccess, setPreviewSuccess] = useState(false);
  // Synchronous guard to avoid concurrent loads (prevents duplicate pages and duplicate keys)
  const genLoadInFlightRef = useRef<boolean>(false);

  // Upload photo flow state
  const [uploadName, setUploadName] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadConsentA, setUploadConsentA] = useState<boolean>(false);
  const [uploadConsentB, setUploadConsentB] = useState<boolean>(false);
  const [uploadSaving, setUploadSaving] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadPreviewUrl = useMemo(() => {
    if (!uploadFile) return '';
    try {
      return URL.createObjectURL(uploadFile);
    } catch {
      return '';
    }
  }, [uploadFile]);
  useEffect(() => {
    return () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    };
  }, [uploadPreviewUrl]);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const resetUploadState = useCallback(() => {
    setUploadName('');
    setUploadFile(null);
    setUploadConsentA(false);
    setUploadConsentB(false);
    setUploadSaving(false);
    setUploadError(null);
  }, []);

  const ensureCompanyId = useCallback(async () => {
    if (companyId) return companyId;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No se encontró el usuario');
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    const idc = (profile as any)?.id_company as string | undefined;
    if (!idc) throw new Error('No se encontró la empresa del usuario');
    setCompanyId(idc);
    return idc;
  }, [companyId]);

  const fetchSavedModels = useCallback(async () => {
    try {
      setLoadingSavedModels(true);
      setSavedModelsError(null);
      const compId = await ensureCompanyId();
      const { data, error } = await supabase
        .from('modelos')
        .select('id, nombre, foto_file_name')
        .eq('id_company', compId)
        .order('nombre');
      if (error) throw error;
      const rows = (data as any[]) || [];
      const withSigned = await Promise.all(
        rows.map(async (m) => {
          let signedUrl: string | undefined;
          const fileName: string | undefined = m?.foto_file_name || undefined;
          if (fileName) {
            try {
              const { data: signed } = await supabase.storage
                .from('modelos')
                .createSignedUrl(`${compId}/full/${fileName}`, 3600);
              if (signed?.signedUrl) signedUrl = signed.signedUrl;
            } catch {}
          }
          return { id: m.id as number, nombre: m.nombre as string, fotoFileName: fileName, signedUrl } as SavedModelItem;
        })
      );
      setSavedModels(withSigned);
    } catch (e) {
      console.error(e);
      setSavedModelsError(e instanceof Error ? e.message : 'Error al cargar modelos guardados');
    } finally {
      setLoadingSavedModels(false);
      setSavedModelsFetched(true);
    }
  }, [ensureCompanyId]);

  const handleOpenEditModel = useCallback((m: SavedModelItem) => {
    setEditingModel(m);
    setEditModelName(m.nombre || '');
    setEditError(null);
    setShowEditModelModal(true);
  }, []);

  const handleCloseEditModel = useCallback(() => {
    setShowEditModelModal(false);
    setEditingModel(null);
    setEditModelName('');
    setEditSaving(false);
    setEditDeleting(false);
    setEditError(null);
  }, []);

  const handleEditModelSave = useCallback(async () => {
    try {
      if (!editingModel) return;
      const newName = editModelName.trim();
      if (!newName || newName === editingModel.nombre) return;
      setEditError(null);
      setEditSaving(true);
      const compId = await ensureCompanyId();
      const { error } = await supabase
        .from('modelos')
        .update({ nombre: newName })
        .eq('id', editingModel.id)
        .eq('id_company', compId);
      if (error) throw error;
      await fetchSavedModels();
      // Si el seleccionado es este, actualizar nombre en memoria (opcional)
      setEditingModel((prev) => (prev ? { ...prev, nombre: newName } : prev));
      setEditSaving(false);
      setShowEditModelModal(false);
    } catch (e: any) {
      console.error('Error guardando modelo:', e);
      setEditError(e?.message || 'No se pudo guardar');
      setEditSaving(false);
    }
  }, [editingModel, editModelName, ensureCompanyId, fetchSavedModels]);

  const handleEditModelDelete = useCallback(async () => {
    try {
      if (!editingModel) return;
      setEditError(null);
      setEditDeleting(true);
      const compId = await ensureCompanyId();
      // Borrar fila
      const { error: delErr } = await supabase
        .from('modelos')
        .delete()
        .eq('id', editingModel.id)
        .eq('id_company', compId);
      if (delErr) throw delErr;
      // Intentar borrar archivo (opcional)
      const fileName = editingModel.fotoFileName;
      if (fileName) {
        try {
          await supabase.storage.from('modelos').remove([`${compId}/full/${fileName}`]);
        } catch (e) {
          console.warn('No se pudo eliminar archivo del modelo', e);
        }
      }
      await fetchSavedModels();
      if (selectedSavedModelId === editingModel.id) setSelectedSavedModelId(null);
      setEditDeleting(false);
      setShowEditModelModal(false);
    } catch (e: any) {
      console.error('Error eliminando modelo:', e);
      setEditError(e?.message || 'No se pudo eliminar');
      setEditDeleting(false);
    }
  }, [editingModel, ensureCompanyId, fetchSavedModels, selectedSavedModelId]);

  const loadGeneratedImages = useCallback(async (isMore = false) => {
    if (genLoadInFlightRef.current) return;
    genLoadInFlightRef.current = true;
    try {
      setGenError(null);
      if (isMore) setGenLoadingMore(true); else setGenLoading(true);
      const compId = await ensureCompanyId();
      const startIndex = isMore ? genPage * GEN_PAGE_SIZE : 0;
      const endIndex = startIndex + GEN_PAGE_SIZE - 1;
      const query = supabase
        .from('imagenes_generadas')
        .select('id, file_name, created_at, active, id_company')
        .eq('active', true)
        .eq('id_company', compId)
        .order('created_at', { ascending: false })
        .range(startIndex, endIndex);
      const { data: imagesData, error } = await query;
      if (error) throw error;

      const processed: GenImageItem[] = await Promise.all((imagesData || []).map(async (img: any) => {
        const optPath = `${compId}/opt/${img.file_name}`;
        const fullPath = `${compId}/full/${img.file_name}`;
        let thumbUrl = '';
        let fullUrl = '';
        try {
          const { data: optSigned } = await supabase.storage.from('generadas').createSignedUrl(optPath, 3600);
          if (optSigned?.signedUrl) thumbUrl = optSigned.signedUrl;
          const { data: fullSigned } = await supabase.storage.from('generadas').createSignedUrl(fullPath, 3600);
          if (fullSigned?.signedUrl) fullUrl = fullSigned.signedUrl;
          if (!thumbUrl && fullUrl) thumbUrl = fullUrl;
          if (!fullUrl && thumbUrl) fullUrl = thumbUrl;
        } catch (e) {
          console.warn('No se pudo firmar imagen generada', e);
        }
        return { id: String(img.id), fileName: img.file_name as string, thumbUrl, fullUrl };
      }));

      if (isMore) {
        setGenImages(prev => {
          const merged = [...prev, ...processed];
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of merged) {
            if (!seen.has(item.id)) { seen.add(item.id); deduped.push(item); }
          }
          return deduped;
        });
        setGenPage(prev => prev + 1);
      } else {
        setGenImages(() => {
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of processed) {
            if (!seen.has(item.id)) { seen.add(item.id); deduped.push(item); }
          }
          return deduped;
        });
        setGenPage(1);
      }
      setGenHasMore((imagesData || []).length === GEN_PAGE_SIZE);
    } catch (e) {
      console.error('Error cargando imágenes generadas:', e);
      setGenError('Error al cargar imágenes. Por favor, volvé a intentarlo.');
    } finally {
      setGenLoading(false);
      setGenLoadingMore(false);
      genLoadInFlightRef.current = false;
    }
  }, [ensureCompanyId, genPage]);
  
  // Upload photo: save handler (after ensureCompanyId/fetchSavedModels are defined to avoid use-before-init)
  const handleUploadSave = useCallback(async () => {
    try {
      setUploadError(null);
      if (!uploadName.trim()) throw new Error('Ingresá un nombre para el modelo');
      if (!uploadFile) throw new Error('Subí una imagen del rostro');
      if (!uploadConsentA || !uploadConsentB) throw new Error('Debés aceptar ambas confirmaciones');
      setUploadSaving(true);
      const compId = await ensureCompanyId();
      // Build target filename
      const ext = (uploadFile.name.split('.').pop() || 'jpg').toLowerCase();
      const base = uploadName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const fileName = `${Date.now()}-${base || 'modelo'}.${ext}`;
      const targetPath = `${compId}/full/${fileName}`;
      // Upload file
      const { error: upErr } = await supabase.storage
        .from('modelos')
        .upload(targetPath, uploadFile, { cacheControl: '3600', upsert: false, contentType: uploadFile.type || 'image/jpeg' });
      if (upErr) throw upErr;
      // Insert DB row (public schema, include id_company)
      const { error: insErr } = await supabase
        .from('modelos')
        .insert({ id_company: compId, nombre: uploadName.trim(), foto_file_name: fileName });
      if (insErr) throw insErr;
      await fetchSavedModels();
      // Close immediately on success
      setShowAddModelCard(false);
      setAddModelMode('choice');
      resetUploadState();
      setUploadSaving(false);
    } catch (e: any) {
      console.error('Error subiendo modelo:', e);
      setUploadError(e?.message || 'No se pudo guardar el modelo');
      setUploadSaving(false);
    }
  }, [uploadName, uploadFile, uploadConsentA, uploadConsentB, ensureCompanyId, fetchSavedModels, resetUploadState]);


  // Lazy-load modelos guardados once when entering 'guardados' mode
  useEffect(() => {
    if (modeloMode === 'guardados' && !savedModelsFetched && !loadingSavedModels) {
      fetchSavedModels();
    }
  }, [modeloMode, savedModelsFetched, loadingSavedModels, fetchSavedModels]);

  // Lock background scroll when the add-model modal is open (robust mobile fix)
  const scrollLockRef = useRef<number>(0);
  useEffect(() => {
    const html = document.documentElement as HTMLElement;
    const body = document.body as HTMLBodyElement;
    if (showAddModelCard) {
      scrollLockRef.current = window.scrollY || window.pageYOffset || 0;
      const prevHtmlOverscroll = html.style.overscrollBehavior;
      const prevBodyOverscroll = body.style.overscrollBehavior as string;
      const prevBodyPosition = body.style.position;
      const prevBodyTop = body.style.top;
      const prevBodyLeft = body.style.left;
      const prevBodyRight = body.style.right;
      const prevBodyWidth = body.style.width;
      (body as any)._prevScrollLock = { prevHtmlOverscroll, prevBodyOverscroll, prevBodyPosition, prevBodyTop, prevBodyLeft, prevBodyRight, prevBodyWidth };
      html.style.overscrollBehavior = 'contain';
      body.style.overscrollBehavior = 'contain';
      body.style.position = 'fixed';
      body.style.top = `-${scrollLockRef.current}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    } else {
      const prev = (body as any)._prevScrollLock as any;
      if (prev) {
        html.style.overscrollBehavior = prev.prevHtmlOverscroll;
        body.style.overscrollBehavior = prev.prevBodyOverscroll;
        body.style.position = prev.prevBodyPosition;
        body.style.top = prev.prevBodyTop;
        body.style.left = prev.prevBodyLeft;
        body.style.right = prev.prevBodyRight;
        body.style.width = prev.prevBodyWidth;
      } else {
        html.style.overscrollBehavior = '';
        body.style.overscrollBehavior = '' as any;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
      }
      window.scrollTo(0, scrollLockRef.current || 0);
    }
  }, [showAddModelCard]);

  // Safety: ensure scroll lock is released if the component unmounts while the modal is open
  useEffect(() => {
    return () => {
      try {
        const html = document.documentElement as HTMLElement;
        const body = document.body as any as (HTMLBodyElement & { _prevScrollLock?: any });
        const prev = body._prevScrollLock;
        const currentTop = parseInt((body.style.top || '0') as string, 10) || 0;
        if (prev || body.style.position === 'fixed') {
          html.style.overscrollBehavior = prev?.prevHtmlOverscroll ?? '';
          body.style.overscrollBehavior = prev?.prevBodyOverscroll ?? '';
          body.style.position = prev?.prevBodyPosition ?? '';
          body.style.top = prev?.prevBodyTop ?? '';
          body.style.left = prev?.prevBodyLeft ?? '';
          body.style.right = prev?.prevBodyRight ?? '';
          body.style.width = prev?.prevBodyWidth ?? '';
          delete body._prevScrollLock;
          if (currentTop) {
            // Restore scroll position
            window.scrollTo(0, -currentTop);
          }
        }
      } catch {
        // noop
      }
    };
  }, []);

  // Infinite scroll observer for generated images grid
  useEffect(() => {
    if (addModelMode !== 'fromGenerated') return;
    const root = genContainerRef.current;
    const sentinel = genLoadMoreRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && genHasMore && !genLoading && !genLoadingMore && !genLoadInFlightRef.current) {
          loadGeneratedImages(true);
        }
      });
    }, { root, rootMargin: '200px 0px', threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [addModelMode, genHasMore, genLoading, genLoadingMore, loadGeneratedImages]);

  // Initial load when entering fromGenerated view
  useEffect(() => {
    if (showAddModelCard && addModelMode === 'fromGenerated' && genImages.length === 0 && !genLoading && !genLoadInFlightRef.current) {
      loadGeneratedImages(false);
    }
  }, [showAddModelCard, addModelMode, genImages.length, genLoading, loadGeneratedImages]);

  const handleUseModelFromGenerated = useCallback(async () => {
    if (!selectedGenImage) return;
    try {
      setPreviewSuccess(false);
      setSavingModelFromImage(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const resp = await fetch('https://ai-agency-dt7d.onrender.com/webhook/guardar_modelo_generado', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, imagen_id: selectedGenImage.id })
      });
      const data = await resp.json().catch(() => ({}));
      if (data?.response === 'success') {
        setSavingModelFromImage(false);
        setPreviewSuccess(true);
        await fetchSavedModels();
        // Auto-cerrar modal tras mostrar éxito
        setTimeout(() => {
          setShowAddModelCard(false);
          setAddModelMode('choice');
          setSelectedGenImage(null);
          setPreviewSuccess(false);
        }, 2000);
      } else {
        alert('No se pudo añadir el modelo');
      }
    } catch (e) {
      console.error('Error al añadir modelo desde imagen generada:', e);
      alert('Error al añadir el modelo');
    } finally {
      if (!previewSuccess) setSavingModelFromImage(false);
    }
  }, [selectedGenImage, fetchSavedModels, previewSuccess]);

  // Ensure preview is visible on mobile by scrolling the container to top
  useEffect(() => {
    if (selectedGenImage && genContainerRef.current) {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches; // lg breakpoint
      if (!isDesktop) {
        try { genContainerRef.current.scrollTo({ top: 0, behavior: 'auto' }); } catch {}
      }
    }
  }, [selectedGenImage]);

  const handleContinue = () => {
    const qp = new URLSearchParams();
    const persistedIds = Array.isArray(savedStateRef.current?.selectedVariantIds)
      ? (savedStateRef.current!.selectedVariantIds as any[])
          .map((s) => String(s))
          .map((s) => s.trim())
          .filter((s) => !!s && s !== '0')
      : [];
    const resolvedIds = (ids && ids.length > 0) ? ids : persistedIds;
    if (resolvedIds.length > 0) qp.set('ids', resolvedIds.join(','));
    qp.set('modo', modo);
    qp.set('composicion', composicion);
    qp.set('tipo', tipo);
    qp.set('formato', formato);
    if (cantidadImagenes) qp.set('cantidad_imagenes', String(cantidadImagenes));
    if (objetivo) qp.set('objetivo', objetivo);
    if (detalles_oferta.trim()) qp.set('detalles_oferta', detalles_oferta.trim());
    qp.set('modelo_mode', modeloMode);
    if (modeloMode === 'personalizado') {
      if (genero) qp.set('genero', genero);
      if (edad) qp.set('edad', edad);
      if (tipoCuerpo) qp.set('tipo_cuerpo', tipoCuerpo);
      if (tonoPiel) qp.set('tono_piel', tonoPiel);
      if (colorPelo) qp.set('color_pelo', colorPelo);
      if (largoPelo) qp.set('largo_pelo', largoPelo);
      if (descModelo.trim()) qp.set('desc_modelo', descModelo.trim());
    } else if (modeloMode === 'guardados' && selectedSavedModelId) {
      const model = savedModels.find((m) => m.id === selectedSavedModelId);
      qp.set('modelo_guardado_id', String(selectedSavedModelId));
      if (model?.nombre) qp.set('modelo_guardado_nombre', model.nombre);
    }
    try {
      if (modeloMode === 'ia') {
        trackInteraction('modelo_automatico');
      } else if (modeloMode === 'personalizado') {
        trackInteraction('modelo_personalizado');
      } else if (modeloMode === 'guardados') {
        trackInteraction('modelo_guardado');
      }
    } catch {}
    if (estudioVirtual) {
      qp.set('estudio_virtual', '1');
    }
    navigate(`/redes-sociales/crear-anuncio/seleccionar-pose?${qp.toString()}`);
  };

  useEffect(() => {
    const trimmedDetalles = detalles_oferta.trim();
    const trimmedDesc = descModelo.trim();
    const selectedSaved = savedModels.find((m) => m.id === selectedSavedModelId);

    const modeloPayload: CrearAnuncioModeloState = { mode: modeloMode };

    if (modeloMode === 'guardados') {
      const resolvedId = selectedSavedModelId
        ? String(selectedSavedModelId)
        : (typeof savedModeloGuardadoId === 'string' && savedModeloGuardadoId ? savedModeloGuardadoId : undefined);
      if (resolvedId) {
        modeloPayload.guardadoId = resolvedId;
      }

      const guardadoNombre = selectedSaved?.nombre || savedModeloGuardadoNombre;
      if (guardadoNombre) {
        modeloPayload.guardadoNombre = guardadoNombre;
      }
    } else if (modeloMode === 'personalizado') {
      if (genero) modeloPayload.genero = genero;
      if (edad) modeloPayload.edad = edad;
      if (tipoCuerpo) modeloPayload.tipoCuerpo = tipoCuerpo;
      if (tonoPiel) modeloPayload.tonoPiel = tonoPiel;
      if (colorPelo) modeloPayload.colorPelo = colorPelo;
      if (largoPelo) modeloPayload.largoPelo = largoPelo;
      if (trimmedDesc) modeloPayload.descripcion = trimmedDesc;
    }

    const allowedObjetivos: CrearAnuncioObjetivo[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
    const objetivoValue = allowedObjetivos.includes(objetivo as CrearAnuncioObjetivo)
      ? (objetivo as CrearAnuncioObjetivo)
      : undefined;

    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      composicion,
      tipo,
      formato,
      cantidadImagenes: cantidadImagenes ?? undefined,
      objetivo: objetivoValue ?? null,
      detallesOferta: trimmedDetalles ? trimmedDetalles : null,
      modelo: modeloPayload,
    });
  }, [
    ids,
    modo,
    composicion,
    tipo,
    formato,
    cantidadImagenes,
    objetivo,
    detalles_oferta,
    modeloMode,
    selectedSavedModelId,
    savedModels,
    genero,
    edad,
    tipoCuerpo,
    tonoPiel,
    colorPelo,
    largoPelo,
    descModelo,
  ]);

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      {/* Header + progress */}
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        {estudioVirtual ? (
          <AppleHeader title="Modelo" onBack={() => navigate(-1)} actionLabel="Siguiente" onAction={handleContinue} />
        ) : (
          <AppleHeader title="Personalizar modelo" />
        )}

        {!estudioVirtual && (
          <div className="mt-4">
            <div className="w-full h-1 bg-gray-200 rounded-full">
              <div className="h-1 bg-blue-500 rounded-full" style={{ width: '75%' }} />
            </div>
          </div>
        )}

        {/* Paso 1: Características del modelo */}

        {/* Características del modelo */}
        <div className="mt-6">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Características del modelo</h2>
          </div>

          {/* Selector de modo de modelo */}
          <div className="mt-3">
            <div className="flex flex-wrap sm:flex-nowrap items-stretch gap-2">
              {/* IA full width on mobile */}
              <button
                type="button"
                onClick={() => setModeloMode('ia')}
                className={`inline-flex items-center justify-center sm:justify-start gap-2 px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto ${modeloMode === 'ia' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
              >
                <Sparkles className="w-4 h-4" />
                Modelo sugerido por IA
              </button>

              {/* Guardados + Personalizado share a row on mobile */}
              <div className="flex w-full sm:w-auto gap-2">
                <button
                  type="button"
                  onClick={() => setModeloMode('guardados')}
                  className={`px-4 h-auto py-2 sm:h-10 sm:py-0 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 ${modeloMode === 'guardados' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                >
                  <User className="w-4 h-4" />
                  <span>Mis modelos</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => setModeloMode('personalizado')}
                  className={`px-4 h-auto py-2 sm:h-10 sm:py-0 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-2 ${modeloMode === 'personalizado' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                >
                  <Sliders className="w-4 h-4" />
                  <span>Modelo personalizado</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
            {modeloMode === 'guardados' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-blue-800">
                <p className="font-medium">Función en estado de prueba.</p>
                <p className="mt-1">Los rostros en las imágenes generadas pueden ser diferentes del rostro del modelo seleccionado.</p>
              </div>
            )}

      {showEditModelModal && showDeleteConfirm && editingModel && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100002]">
          <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-6">
            <div className="flex items-center mb-4 text-red-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-semibold">Eliminar modelo</h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="ml-auto text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-700 mb-4">¿Estás seguro que querés eliminar este modelo? Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => { await handleEditModelDelete(); setShowDeleteConfirm(false); }}
                disabled={editDeleting}
                className={`h-10 px-4 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-white ${editDeleting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {editDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar modelo guardado */}
      {showEditModelModal && editingModel && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100001]">
          <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar modelo</h3>
              <button onClick={handleCloseEditModel} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            {editError && <div className="mb-3 text-sm text-red-600">{editError}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editModelName}
                  onChange={(e) => setEditModelName(e.target.value)}
                  className="w-full h-10 px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  placeholder="Nombre del modelo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto</label>
                <div className="w-full rounded-2xl border border-gray-200 overflow-hidden bg-white">
                  {editingModel.signedUrl ? (
                    <img src={editingModel.signedUrl} alt={editingModel.nombre || 'Modelo'} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={editSaving || editDeleting}
                className={`h-10 px-4 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-white ${editDeleting ? 'bg-red-400' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {editDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseEditModel}
                  className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleEditModelSave}
                  disabled={editSaving || !editModelName.trim() || editModelName.trim() === (editingModel?.nombre || '')}
                  className={`h-10 px-4 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-white ${editSaving || !editModelName.trim() || editModelName.trim() === (editingModel?.nombre || '') ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {editSaving ? (
                    <span className="inline-flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2"/>Guardando...</span>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          </div>

          {/* Sección Modelos guardados */}
          {modeloMode === 'guardados' && (
            <div className="mt-4">
              {loadingSavedModels ? (
                <div className="text-sm text-gray-600">Cargando modelos...</div>
              ) : savedModelsError ? (
                <div className="text-sm text-red-600">{savedModelsError}</div>
              ) : savedModels.length === 0 ? (
                <div>
                  <div className="text-sm text-gray-600">No tenés modelos guardados todavía.</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
                    {/* Tile: Añadir modelo */}
                    <button
                      type="button"
                      onClick={() => setShowAddModelCard(true)}
                      className="relative text-left bg-white border-0 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
                    >
                      <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                        <div className="w-full aspect-square bg-white flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-500 text-white flex items-center justify-center transition-colors group-hover:bg-blue-600">
                            <Plus className="w-8 h-8 sm:w-9 sm:h-9" />
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="text-sm text-gray-900 truncate font-medium py-1">Añadir modelo</p>
                      </div>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3">
                  {/* Tile: Añadir modelo */}
                  <button
                    type="button"
                    onClick={() => setShowAddModelCard(true)}
                    className="relative text-left bg-white border-0 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
                  >
                    <div className="relative pt-0 px-0 sm:pt-3 sm:px-3">
                      <div className="w-full aspect-square bg-white flex items-center justify-center rounded-b-2xl sm:rounded-lg">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-500 text-white flex items-center justify-center transition-colors group-hover:bg-blue-600">
                          <Plus className="w-8 h-8 sm:w-9 sm:h-9" />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm text-gray-900 truncate font-medium py-1">Añadir modelo</p>
                    </div>
                  </button>
                  {savedModels.map((m) => {
                    const selected = selectedSavedModelId === m.id;
                    const imgSrc = m.signedUrl || 'https://via.placeholder.com/300x300?text=Modelo';
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelectedSavedModelId(selected ? null : m.id)}
                        className={`relative w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                      >
                        <div className="relative overflow-hidden rounded-xl">
                          {m.signedUrl ? (
                            <img src={imgSrc} alt={m.nombre || 'Modelo'} loading="lazy" className="w-full h-40 object-cover" />
                          ) : (
                            <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          {selected && (
                            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 mt-2 font-semibold">{m.nombre || 'Modelo'}</p>
                        {selected && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); handleOpenEditModel(m); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); handleOpenEditModel(m); } }}
                            className="absolute bottom-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 cursor-pointer"
                            aria-label="Editar modelo"
                          >
                            <Pencil className="w-4 h-4" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Sección Modelo personalizado */}
          {modeloMode === 'personalizado' && (
            <div className="mt-4 space-y-5">
              {/* Género */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Género</label>
                <div className="flex gap-2">
                  {(['Hombre', 'Mujer'] as Genero[]).map((g) => (
                    <button
                      key={g || 'none'}
                      type="button"
                      onClick={() => setGenero(genero === g ? '' : g)}
                      className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${genero === g ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Edad */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Edad</label>
                <div className="flex flex-wrap gap-2">
                  {(['Bebé', 'Niño', 'Joven (25 años)', 'Adulto/a(30 a 40 años)', 'Mayor'] as Edad[]).map((e) => (
                    <button
                      key={e || 'none'}
                      type="button"
                      onClick={() => setEdad(edad === e ? '' : e)}
                      className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${edad === e ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo de cuerpo */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Tipo de cuerpo</label>
                <div className="flex flex-wrap gap-2">
                  {(['Promedio', 'Talle Grande', 'Talle Extra Grande'] as TipoCuerpo[]).map((c) => (
                    <button
                      key={c || 'none'}
                      type="button"
                      onClick={() => setTipoCuerpo(tipoCuerpo === c ? '' : c)}
                      className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${tipoCuerpo === c ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tono de piel */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Tono de piel</label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setTonoPiel('')}
                    className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${tonoPiel === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Cualquiera
                  </button>
                  {skinToneOptions.map(({ label, color }) => (
                    <button
                      key={label}
                      type="button"
                      title={label}
                      onClick={() => setTonoPiel(tonoPiel === label ? '' : label)}
                      className={`w-9 h-9 rounded-full border ${tonoPiel === label ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-300'} flex items-center justify-center`}
                      style={{ backgroundColor: color }}
                    >
                      {tonoPiel === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-blue-500" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color de pelo */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Color de pelo</label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setColorPelo('')}
                    className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${colorPelo === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Cualquiera
                  </button>
                  {hairColorOptions.map(({ label, color }) => (
                    <button
                      key={label}
                      type="button"
                      title={label}
                      onClick={() => setColorPelo(colorPelo === label ? '' : label)}
                      className={`w-9 h-9 rounded-full border ${colorPelo === label ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-300'} flex items-center justify-center`}
                      style={{ backgroundColor: color }}
                    >
                      {colorPelo === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-blue-500" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Largo de pelo */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Largo de pelo</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setLargoPelo('')}
                    className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${largoPelo === '' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Cualquiera
                  </button>
                  {['Rapado', 'Corto', 'Medio', 'Largo'].map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setLargoPelo(largoPelo === l ? '' : l)}
                      className={`px-4 h-10 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${largoPelo === l ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Descripción adicional */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">Descripción adicional</label>
                <textarea
                  rows={3}
                  value={descModelo}
                  onChange={(e) => setDescModelo(e.target.value)}
                  placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados"
                  className="w-full rounded-md border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer actions (desktop) */}
        {!estudioVirtual && (
          <div className="mt-6 hidden sm:flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>

            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm bg-blue-500 hover:bg-blue-600 text-white`}
              onClick={handleContinue}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      {!estudioVirtual && (
        <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />
      )}

      {/* Mobile bottom bar */}
      {!estudioVirtual && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Volver
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm bg-blue-500 hover:bg-blue-600 text-white`}
              onClick={handleContinue}
            >
              Continuar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Card UI para añadir modelo */}
      {showAddModelCard && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-[100000] p-6 sm:p-4 overscroll-none">
          <div className={`bg-white rounded-2xl shadow-sm w-full ${addModelMode === 'fromGenerated' ? 'max-w-6xl h-[calc(100dvh-4rem)] sm:h-auto sm:max-h-[90dvh]' : 'max-w-2xl'} overflow-hidden p-0 relative flex flex-col`}>          
            {/* Header */}
            <div className="px-6 pt-5 pb-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {addModelMode !== 'choice' && (
                  <button onClick={() => { setAddModelMode('choice'); setSelectedGenImage(null); resetUploadState(); }} className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" aria-label="Volver">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <h3 className="text-lg font-semibold text-gray-900">Añadir modelo</h3>
              </div>
              <button
                onClick={() => { setShowAddModelCard(false); setAddModelMode('choice'); setSelectedGenImage(null); setGenImages([]); setGenPage(0); resetUploadState(); }}
                className="p-2 text-gray-400 hover:text-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            {addModelMode === 'choice' && (
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">Elegí cómo querés agregar un modelo para usar en tus anuncios.</p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => { setAddModelMode('fromGenerated'); }}
                    className="w-full text-left border border-gray-200 rounded-2xl p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">Guardar modelo de imagen generada</p>
                        <p className="text-sm text-gray-600 mt-0.5">Guarda los datos faciales de un modelo presente en una imagen generada previamente.</p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => { resetUploadState(); setAddModelMode('uploadPhotos'); }}
                    className="w-full text-left border border-gray-200 rounded-2xl p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <UploadCloud className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">Subir fotos de mi modelo</p>
                        <p className="text-sm text-gray-600 mt-0.5">Sube imágenes de un modelo asociado a la marca.</p>
                      </div>
                    </div>
                  </button>

                </div>
              </div>
            )}

            {addModelMode === 'uploadPhotos' && (
              <div className="p-6">
                <div className="max-w-3xl mx-auto">
                  <div className="space-y-4">
                    {uploadError && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{uploadError}</div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del modelo</label>
                      <input
                        type="text"
                        value={uploadName}
                        onChange={(e) => setUploadName(e.target.value)}
                        placeholder="Ej: Juan Pérez"
                        className="w-full h-10 px-4 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Imagen</label>
                      <p className="text-xs text-gray-600 mb-2 sm:hidden">Subí una imagen del rostro de la persona que quieres usar como modelo. La imagen debe ser de frente, desde los hombros hacia arriba, con buena iluminación.</p>
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-center gap-4">
                        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-40 md:h-40 bg-white border border-gray-200 rounded-2xl flex items-center justify-center overflow-hidden">
                          {uploadPreviewUrl ? (
                            <img src={uploadPreviewUrl} alt="Vista previa" className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-gray-400 text-sm">Sin imagen</div>
                          )}
                        </div>
                        <div className="flex-1 w-full">
                          <input
                            id="upload-model-file"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            className="hidden"
                          />
                          {!uploadPreviewUrl && (
                            <label
                              htmlFor="upload-model-file"
                              className="inline-flex w-full sm:w-auto justify-center items-center px-3 py-3 sm:py-2 shadow-sm text-sm font-normal rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer bg-blue-500 text-white hover:bg-blue-600"
                            >
                              <ImagePlus className="w-4 h-4 mr-2" />
                              Subir imagen
                            </label>
                          )}
                          <ul className="hidden sm:block text-xs text-gray-600 mt-2 list-disc pl-5 space-y-1">
                            <li>Subí una imagen del rostro de la persona que quieres usar como modelo.</li>
                            <li>La imagen debe ser de frente, desde los hombros hacia arriba, con buena iluminación.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500" checked={uploadConsentA} onChange={(e) => setUploadConsentA(e.target.checked)} />
                        <span>Confirmo que esta persona accedió a que yo utilice su imagen, y dio su consentimiento para que se generaran imágenes con su rostro</span>
                      </label>
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500" checked={uploadConsentB} onChange={(e) => setUploadConsentB(e.target.checked)} />
                        <span>Acepto toda responsabilidad legal relacionada al uso de la imagen de esta persona para la generación de imágenes en Atlas</span>
                      </label>
                    </div>

                    <div className="pt-2 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => { setAddModelMode('choice'); resetUploadState(); }}
                        className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleUploadSave}
                        disabled={!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving}
                        className={`h-10 px-4 rounded-full ${(!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving) ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal`}
                      >
                        {uploadSaving ? (
                          <span className="inline-flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2"/>Guardando...</span>
                        ) : (
                          'Guardar modelo'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {addModelMode === 'fromGenerated' && (
              <div ref={genContainerRef} className="flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className={`lg:col-span-2 ${selectedGenImage ? 'hidden lg:block' : ''}`}>
                    <div className="border border-gray-200 rounded-2xl overflow-hidden p-2">
                      {genError && <div className="text-sm text-red-600 p-3">{genError}</div>}
                      {genLoading && genImages.length === 0 ? (
                        <div className="flex items-center justify-center h-48 text-gray-600"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando imágenes...</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {genImages.map((g) => (
                            <button
                              key={g.id}
                              type="button"
                              onClick={() => setSelectedGenImage(g)}
                              className={`relative rounded-md overflow-hidden border ${selectedGenImage?.id === g.id ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} focus:outline-none`}
                            >
                              {g.thumbUrl ? (
                                <img src={g.thumbUrl} alt={`Img ${g.id}`} className="w-full aspect-square object-cover" loading="lazy" />
                              ) : (
                                <div className="w-full aspect-square bg-gray-100" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {(genHasMore || genLoadingMore) && (
                        <div ref={genLoadMoreRef} className="py-3 text-center text-sm text-gray-600">
                          {genLoadingMore ? <span className="inline-flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2"/>Cargando más...</span> : 'Desplazate para cargar más'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`lg:col-span-1 ${selectedGenImage ? '' : 'hidden lg:block'}`}>
                    <div className="relative rounded-2xl p-3 flex flex-col min-h-[200px] h-[70dvh] lg:h-[calc(90dvh-8rem)] lg:sticky lg:top-0 lg:overflow-auto">
                      {(savingModelFromImage || previewSuccess) && (
                        <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4">
                          {savingModelFromImage && !previewSuccess && (
                            <>
                              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
                              <p className="mt-3 text-sm text-gray-700">Guardando modelo...</p>
                            </>
                          )}
                          {previewSuccess && (
                            <>
                              <CheckCircle2 className="w-12 h-12 text-green-600" />
                              <p className="mt-2 text-sm text-gray-800">Modelo añadido</p>
                            </>
                          )}
                        </div>
                      )}
                      {selectedGenImage ? (
                        <>
                          <div className="w-full mb-3 flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                            <img src={selectedGenImage.fullUrl || selectedGenImage.thumbUrl} alt="Vista previa" className="w-full h-[55dvh] sm:h-auto object-contain rounded" />
                          </div>
                          <div className="w-full mt-auto flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={handleUseModelFromGenerated}
                              disabled={savingModelFromImage}
                              className={`inline-flex items-center gap-2 h-10 px-4 rounded-full ${savingModelFromImage ? 'bg-blue-400' : 'bg-blue-500 hover:bg-blue-600'} text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto justify-center font-normal`}
                            >
                              <Scan className="w-4 h-4" />
                              <span>Usar el modelo de esta imagen</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedGenImage(null)}
                              className="w-full h-10 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 lg:hidden inline-flex items-center justify-center gap-2"
                            >
                              <ChevronLeft className="w-4 h-4" />
                              <span>Volver a la galería</span>
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-600">Seleccioná una imagen para ver la vista previa.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalizarModeloAnuncio;